import { useCallback, useEffect, useState } from 'react'
import usePrevious from 'hooks/usePrevious'
import request from 'services/request'
import { NftRecordResModel, NftRecordReqModel } from 'services/types/Market'
import {
  AccountAssets,
  AccountRecord,
  AccountRecordReq,
  AmountQuoteResModel,
  TokenExtractRes,
  UserAssetsBalanceResModel,
  UserCabitalInfoResModel,
} from 'services/types/User'
import { useDeepCompareEffect } from 'hooks/useDeepCompareEffect'
// import accountJson from './account-record.json'
import { ResponseBase } from 'services/responseBase'

// 获取 My Records
export function useMyAssets(data: NftRecordReqModel): NftRecordResModel | undefined {
  const [record, setRecord] = useState<NftRecordResModel>()
  const previousBanners = usePrevious(record)
  useEffect(() => {
    if (!record && !previousBanners) {
      request.post<NftRecordResModel>('nft/myRecord', data).then(result => {
        setRecord(result.data)
      })
    }
  }, [record, previousBanners, data])
  return record
}

// 获取用户资产列表
export function getUserAssets() {
  return request.get<AccountAssets>('assets/user-account/v1/account-balance')
}

// 获取用户交易记录
export function useUserRecords(data: AccountRecordReq): {
  userRecords: AccountRecord | undefined
  loading: boolean
  hasNextPage: boolean
} {
  const [userRecords, setRecords] = useState<AccountRecord>()
  const [loading, setLoading] = useState<boolean>(false)
  const [hasNextPage, setHasNextPage] = useState<boolean>(false)
  useDeepCompareEffect(() => {
    if (data) {
      setLoading(true)
      request
        .post<AccountRecord>('assets/user-account/v1/account-record', data)
        .then(result => {
          setLoading(false)
          if (result.code === 200) {
            if (data.currentPage === 1) {
              // result.data.records = result.data.records.map((item, index) => {
              //   if (index === 10 && item?.fromAssetInfoRsp?.tokenSymbol) {
              //     item.fromAssetInfoRsp.tokenSymbol = 'gPKTK'
              //   }
              //   return item
              // })
              setRecords(result.data)
            } else {
              setRecords(order =>
                order ? { ...order, records: [...order.records, ...result.data.records] } : result.data
              )
            }
            const hasNext = result.data.current < result.data.pages
            setHasNextPage(hasNext)
          } else {
            setLoading(false)
            setHasNextPage(false)
          }
        })
        .catch(() => {
          setLoading(false)
          setHasNextPage(false)
        })
    }
  }, [data])
  return { loading, userRecords, hasNextPage }
}

// 提取
export function tokenExtract(data: {
  coinId: number
  chainId: number
  extractAddress: string
  amount: string
  recaptchaToken: string
}) {
  return request.post<TokenExtractRes>(`assets/token/v1/token-extract`, data)
}

// swap
export function tokenSwap(data: {
  coinId: number
  fromChainId: number
  toChainId: number
  amount: string
  recaptchaToken: string
}) {
  return request.post<TokenExtractRes>(`assets/token/v1/token-swap`, data)
}

// 提取验证
export function emailValidator(data: { emailCode: string; code: string }): Promise<ResponseBase> {
  return request.post<ResponseBase>(`assets/token/v1/token-extract-email-validator`, data)
}

// 提取验证重发
export function resendCode(code: string) {
  return request.post<TokenExtractRes>(`assets/token/v1/resend-code/${code}`)
}

// 获取用户资产列表
export function useSymbolBalance(symbol: string): [string, boolean] {
  const [balance, setBalance] = useState<string>('0')
  // const dispatch = useAppDispatch()
  const [loading, setLoading] = useState<boolean>(true)
  const previousBanners = usePrevious(balance)
  useEffect(() => {
    if (!previousBanners) {
      request
        .get<string>(`assets/amount/v1/balances/${symbol.toLocaleUpperCase()}`)
        .then(result => {
          setBalance(result.data)
          // dispatch(updateUserAssets(result.data))
          setLoading(false)
        })
        .catch(() => setLoading(false))
    }
  }, [balance, symbol, previousBanners])
  return [balance, loading]
}

// 获取 报价
export function useAmountQuote(from: string, to: string, count: number): AmountQuoteResModel | null {
  const [result, setResult] = useState<AmountQuoteResModel | null>(null)
  useEffect(() => {
    if (count === 0 && to !== from) {
      request.get<AmountQuoteResModel>(`assets/amount/v1/quote/${from}-${to}`).then(result => {
        if (result.code === 200) {
          setResult(result.data)
        } else {
          setResult(null)
        }
      })
    }
    if (to === from) {
      setResult(null)
    }
  }, [to, from, count])
  return result
}

// 获取用户cabital link 信息
export function userCabitalInfo() {
  return request.post<UserCabitalInfoResModel>(`profile/v1/user/cabital/link/info`)
}

// 获取用户cabital 余额list
export function userCabitalBalances() {
  return request.get<Array<UserAssetsBalanceResModel>>(`assets/amount/v1/balances`)
}

// cabital link 获取url
export function getCabitalLink(type: string, step: string) {
  return request.get<string>(`profile/v1/link/cabital/${type}/${step}`)
}

interface CreateOrderReq {
  coinType: string
  tokenType: string
  network?: string
  coinAmount: string
  tokenAmount?: string
  quoteId: string
  reversedQuote: string
  fromAddress?: string
  toAddress?: string
  source?: string
}

// 法币入金：生成订单
export function createOrder(data: CreateOrderReq) {
  return request.post<string>(`assets/amount/v1/create/order`, data)
}

// 法币入金确认
export function cabitalTokenBuy(orderKey: string, otp: string) {
  return request.post(`assets/amount/v1/token/buy`, { orderKey, otp })
}

// cabital 回调接口
export function cabitalCallBack() {
  return request.get(`profile/v1/call_back/cabital`)
}

export function useUserSwapNum() {
  const [swapNum, setSwapNum] = useState<{
    totalTimes: number
    usageTimes: number
  }>({
    totalTimes: 0,
    usageTimes: 0,
  })
  const fetchList = useCallback((reFetch?: boolean) => {
    if (reFetch) {
      request.get(`assets/token/v1/queryUserSwapNum`).then((result: ResponseBase) => {
        if (result?.success) {
          setSwapNum(result.data)
        }
      })
    }
  }, [])
  useEffect(() => {
    fetchList(true)
  }, [fetchList])
  const reFetch = () => {
    fetchList(true)
  }

  return { swapNum, reFetch }
}

interface RATE {
  rate: number
  feeRate: number
  burnRate: number
  slide: number
  dexRate: string
}
export function useUsdtPktkRate() {
  const [rate, setRate] = useState<RATE>()
  useEffect(() => {
    request.post(`assets/token/v1/usdt-pktk-rate`).then(res => {
      if (res.code === 200) {
        setRate(res.data)
      }
    })
  }, [])
  return rate
}

export interface RATE_INCLUDE_COMMISSION {
  amount?: number
  fromCoinId?: number
  toCoinId?: number
}
export function useUsdtPktkIncludeCommissionRate({ amount = 1, fromCoinId, toCoinId }: RATE_INCLUDE_COMMISSION) {
  const [convertorAmount, setConvertorAmount] = useState<number>()
  const preFromCoinId = usePrevious(fromCoinId)
  useDeepCompareEffect(() => {
    if (amount && fromCoinId && toCoinId && preFromCoinId !== fromCoinId) {
      request.post(`assets/token/v1/prepareCalculate`, { amount, fromCoinId, toCoinId }).then(res => {
        if (res.code === 200) {
          setConvertorAmount(res?.data?.amount)
        }
      })
    }
  }, [amount, fromCoinId, toCoinId])
  return convertorAmount
}

export function coinSwap(params: {
  fromCoinId: number
  toCoinId: number
  fromChainId: number
  toChainId: number
  amount: number
  toAmount: number
  recaptchaToken?: string
}) {
  return request.post(`assets/token/v1/coin-swap`, params)
}
export function useUserUsdtAndPktkSwapNum() {
  const [swapNum, setSwapNum] = useState<{ totalTimes: number; usageTimes: number }>()
  useEffect(() => {
    request.post(`assets/token/v1/queryUserUsdtAndPktkSwapNum`).then(res => {
      if (res.code === 200) {
        setSwapNum(res.data)
      }
    })
  }, [])
  return swapNum
}

export function prepareCalculate(params: { fromCoinId: number; toCoinId: number; amount: number }) {
  return request.post(`assets/token/v1/prepareCalculate`, params)
}

export function useSwapConfig() {
  const [config, setConfig] = useState<any>()
  useEffect(() => {
    request.post(`assets/token/v1/asset/config`).then(res => {
      if (res.code === 200) {
        setConfig(res.data)
      }
    })
  }, [])
  return config
}

// cabital 回调接口
export function useTokenWithdrawLimit(id: string) {
  const [withdrawLimit, setWithdrawLimit] = useState<{ outMaxnum: number; usageTimes: number }>()
  const fetchConfig = useCallback(
    (reFetch?: boolean) => {
      if (reFetch && id) {
        request.get(`assets/token/v1/config/${id}`).then(result => {
          if (result.code === 200) {
            setWithdrawLimit(result.data)
          }
        })
      }
    },
    [id]
  )
  useEffect(() => {
    fetchConfig(true)
  }, [fetchConfig])
  const reFetch = () => {
    fetchConfig(true)
  }
  return { withdrawLimit, reFetch }
}

export interface TOKEN_SWAP_ONE_MODEL {
  maxLimit: number
  minLimit: number
  maxNum: number
  usageTimes: number
}
export interface TOEKN_SWAP_CONFIG_MODEL {
  [key: string]: TOKEN_SWAP_ONE_MODEL
}
// $pktk gPktk USDT
export function useSwapConfigInfo() {
  const [swapConfigInfo, setSwapConfigInfo] = useState<TOEKN_SWAP_CONFIG_MODEL>()
  const fetchConfig = useCallback((reFetch?: boolean) => {
    if (reFetch) {
      request.get(`assets/swapConfig/queryList`).then((result: ResponseBase) => {
        if (result.code === 200) {
          setSwapConfigInfo(result.data || {})
          console.log('result.data', result.data)
        }
      })
    }
  }, [])
  useEffect(() => {
    fetchConfig(true)
  }, [fetchConfig])
  const reFetch = () => {
    fetchConfig(true)
  }

  return { swapConfigInfo, reFetch }
}
